import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import PhoneNumber from '../PhoneNumber/PhoneNumber';
import { validateField, setTouched, setFocused, setValue } from '@nsr/react-form';
import get from 'lodash/get';
import toPath from 'lodash/toPath';
import { useIntl } from 'react-intl';
import { batch, useDispatch, useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import useRendersCount from '@hedtech/userenderscount';
import { isPossiblePhoneNumber, parsePhoneNumber } from 'react-phone-number-input';
import { formatPhoneNumber } from '../Helpers/PhoneHelper';

const splitCodeAndNumber = (value) => {
    if (typeof value === 'undefined') {
        return '';
    }
    const phoneNumber = parsePhoneNumber(value);
    const splitPhone = {
        code: '',
        number: ''
    };
    if (phoneNumber) {
        splitPhone.code = phoneNumber.countryCallingCode;
        splitPhone.number = phoneNumber.nationalNumber;
        return splitPhone;
    }
    else {
        return '';
    }
};

const combineCodeAndNumber = (value, country) => {
    return formatPhoneNumber(value, country, false);
};
const FormPhoneField = props => {
    const {
        formatFromStore = combineCodeAndNumber,
        formatToStore = splitCodeAndNumber,
        helperText: helperTextProp = null,
        ignoreWhitespace = false,
        invalidFormatText = '',
        label = '',
        name: nameProp,
        onBlur: onBlurProp,
        onFocus: onFocusProp,
        required = false,
        requiredErrorTextId = 'validateRequired',
        validate: validateProp,
        country = '',
        ...rest
    } = props;
    useRendersCount(`FormPhoneField( ${nameProp} )`);
    const { formatMessage } = useIntl();
    const dispatch = useDispatch();
    const selectValue = useMemo(() => {
        return createSelector(
            state => get(state.form.values, toPath(nameProp)),
            value => value ? formatFromStore(value, country) : '',
        );
    }, [nameProp, formatFromStore, country]);
    const value = useSelector(selectValue);
    const validate = useMemo(() => {
        const isRequired = value => {
            if (!required) {
                return undefined;
            }
            value = ignoreWhitespace && value ? value.trim() : value;
            if (!value) {
                return formatMessage({ id: requiredErrorTextId });
            }
            if (!isPossiblePhoneNumber(value)) {
                return invalidFormatText;
            }
        };
        const composeValidators = (fns = []) => (value, allValues, meta) => {
            const validators = [isRequired, ...fns];
            let error;
            for (let x = 0; x < validators.length; x++) {
                error = validators[x](value, allValues, meta);
                if (error) {
                    break;
                }
            }
            return error;
        };
        let validatorsArray = validateProp;
        if (validateProp && typeof validateProp === 'function') {
            validatorsArray = [validateProp];
        }
        return composeValidators(validatorsArray);
    }, [formatMessage, ignoreWhitespace, required, validateProp, requiredErrorTextId]);
    useEffect(() => {
        dispatch(validateField({ name: nameProp, validate, value }));
    }, [dispatch, nameProp, validate, value]);
    const onChange = useCallback(value => {
        dispatch(setValue({
            name: nameProp,
            value: formatToStore(value),
        }));
    }, [dispatch, formatToStore, nameProp]);
    const onBlur = useCallback(e => {
        batch(() => {
            dispatch(setTouched({ name: e.target.name, value: true }));
            dispatch(setFocused({ name: e.target.name, value: false }));
        });
        onBlurProp && onBlurProp(e);
    }, [dispatch, onBlurProp]);
    const onFocus = useCallback(e => {
        dispatch(setFocused({ name: e.target.name, value: true }));
        onFocusProp && onFocusProp(e);
    }, [dispatch, onFocusProp]);
    return (
        <PhoneNumber
            {...rest}
            helperText={helperTextProp}
            label={label}
            name={nameProp}
            onBlur={onBlur}
            onChange={onChange}
            onFocus={onFocus}
            required={required}
            value={value}
        />
    );
};
FormPhoneField.propTypes = {
    alwaysShowError: PropTypes.bool,
    formatFromStore: PropTypes.func,
    formatToStore: PropTypes.func,
    helperText: PropTypes.node,
    ignoreWhitespace: PropTypes.bool,
    invalidFormatText: PropTypes.string,
    label: PropTypes.string,
    name: PropTypes.string.isRequired,
    onBlur: PropTypes.func,
    onFocus: PropTypes.func,
    required: PropTypes.bool,
    requiredErrorTextId: PropTypes.string,
    validate: PropTypes.oneOfType([PropTypes.func, PropTypes.arrayOf(PropTypes.func)]),
    country: PropTypes.string
};
export default FormPhoneField;
